import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";

/**
 * CustomerService provides data access to the customers table in Supabase
 */
export const CustomerService = {
  /**
   * Get all customers from Supabase - mimics Supabase's return format { data, error }
   */
  async getAll() {
    const { data, error } = await supabase
      .from("customers")
      .select("*")
      .order("created_at", { ascending: false });

    return { data, error };
  },

  /**
   * Get customers by a specific field
   */
  async getByField(field: string, value: string) {
    const { data, error } = await supabase
      .from("customers")
      .select("*")
      .eq(field, value)
      .order("created_at", { ascending: false });

    return { data, error };
  },

  /**
   * Get a single customer by ID
   */
  async getById(id: string) {
    const { data, error } = await supabase
      .from("customers")
      .select("*")
      .eq("id", id)
      .single();

    return { data, error };
  },

  /**
   * Search customers by name or address
   */
  async search(query: string) {
    const { data, error } = await supabase
      .from("customers")
      .select("*")
      .or(`first_name.ilike.%${query}%,last_name.ilike.%${query}%,email.ilike.%${query}%,customer_number.ilike.%${query}%`)
      .order("created_at", { ascending: false });

    return { data, error };
  },

  /**
   * Create a new customer - mimics Supabase's return format { data, error }
   * Uses first_name, last_name fields as per actual Supabase schema
   * Auto-populates created_by from auth context if not provided
   */
  async create(customerData: {
    first_name: string;
    last_name?: string | null;
    email?: string | null;
    phone?: string | null;
    customer_number?: string | null;
    // Accept either UI alias (street_address/zip) or real column names (address_line1/postal_code)
    street_address?: string | null;
    address_line1?: string | null;
    address_line2?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
    postal_code?: string | null;
    notes?: string | null;
  }) {
    // Resolve organization_id (required NOT NULL column)
    const organization_id = await getCurrentOrganizationId();

    // Map UI field aliases to real DB column names, strip fields not in schema
    const { street_address, zip, ...rest } = customerData as any;
    const row: Record<string, unknown> = {
      ...rest,
      organization_id,
      ...(street_address !== undefined ? { address_line1: street_address } : {}),
      ...(zip !== undefined ? { postal_code: zip } : {}),
    };

    const { data, error } = await supabase
      .from("customers")
      .insert([row])
      .select()
      .single();

    return { data, error };
  },

  /**
   * Update an existing customer
   */
  async update(id: string, updates: Record<string, unknown>) {
    const { data, error } = await supabase
      .from("customers")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    return { data, error };
  },

  /**
   * Delete a customer
   */
  async delete(id: string) {
    const { error } = await supabase
      .from("customers")
      .delete()
      .eq("id", id);

    return { error };
  },
};
